var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx } from "react/jsx-runtime";
import { BellIcon, CreditCardIcon, EnvelopeIcon, UserCircleIcon } from '@heroicons/react/24/outline';
import { Cog6ToothIcon, TagIcon, UsersIcon } from '@heroicons/react/24/solid';
import { getPerformanceInsightsSections } from '@web/components/PerformanceReview/insightsSections';
import { BellSettingsIcon } from '@web/components/ui/icons/BellSettingsIcon';
import EventLogIcon from '@web/components/ui/icons/EventLogIcon';
import GeminiIcon from '@web/components/ui/icons/GeminiIcon';
import GoogleChatIcon from '@web/components/ui/icons/GoogleChatIcon';
import GoogleDriveIcon from '@web/components/ui/icons/GoogleDriveIcon';
import GoogleMeetIcon from '@web/components/ui/icons/GoogleMeetIcon';
import { PerformanceReviewSectionIcon } from '@web/components/ui/icons/PerformanceReviewSections';
import SearchDriveIcon from '@web/components/ui/icons/SearchDriveIcon';
import SearchMeetIcon from '@web/components/ui/icons/SearchMeetIcon';
import { getUrl } from '@web/router/getUrl';
import { appsUrl, dashboardUrl, docAlertsUrl, performanceInsightsUrl, settingsUrl } from '@web/router/urls';
import { hasScopeFun } from '@web/utility';
import { filtersToQuery } from '@web/utility/hooks/useFilters';
import { appsActiveTabs, docAlertsActiveTabs, settingsActiveTabs } from '@web/utility/tabs';
import { PerformanceReviewSectionsEnum, ScopeUIPageEnum } from '@workalizer/shared';
import { t } from 'i18next';
export var getMenu = function (currentUrl, scopes, currentFilters, currentPageSection, canAccessManagerSection) {
    if (canAccessManagerSection === void 0) { canAccessManagerSection = false; }
    var isCurrentUrl = function (url) { return currentUrl === url; };
    var isPerformanceInsightsPage = isCurrentUrl(performanceInsightsUrl);
    var isCurrentPerformanceInsightsSection = function (section) {
        return isPerformanceInsightsPage && currentPageSection === section;
    };
    var buildPerformanceInsightsUrl = function (pageSection) {
        var filters = __assign(__assign(__assign({}, ((currentFilters === null || currentFilters === void 0 ? void 0 : currentFilters.user) && { user: currentFilters.user })), ((currentFilters === null || currentFilters === void 0 ? void 0 : currentFilters.orgUnit) && { orgUnit: currentFilters.orgUnit })), { pageSection: pageSection });
        var query = filtersToQuery(filters);
        return "".concat(performanceInsightsUrl, "?").concat(query);
    };
    var performanceInsightsSections = getPerformanceInsightsSections(canAccessManagerSection);
    return [
        {
            key: 'dashboard',
            name: t('menu.dashboard'),
            url: dashboardUrl,
            current: currentUrl === dashboardUrl
        },
        {
            key: 'performance-insights',
            name: t('menu.performanceInsights'),
            url: "".concat(performanceInsightsUrl, "?pageSection=").concat(PerformanceReviewSectionsEnum.OVERVIEW),
            current: currentUrl.startsWith(performanceInsightsUrl),
            children: performanceInsightsSections.map(function (section) { return ({
                name: t("app.performanceReview.employeePerformanceReview.".concat(section)),
                url: buildPerformanceInsightsUrl(section),
                current: isCurrentPerformanceInsightsSection(section),
                icon: section === PerformanceReviewSectionsEnum.MANAGER_ONLY ? (_jsx(UserCircleIcon, { className: "w-5 h-5 inline-block" })) : (_jsx(PerformanceReviewSectionIcon, { section: section, className: "w-5 h-5 inline-block" }))
            }); })
        },
        {
            key: 'apps',
            name: t('menu.appsUsage'),
            url: getUrl(appsUrl),
            current: currentUrl.startsWith(appsUrl),
            children: [
                {
                    name: t('app.apps.gmail.title'),
                    url: getUrl(appsUrl, appsActiveTabs.gmail),
                    current: isCurrentUrl(getUrl(appsUrl, appsActiveTabs.gmail)),
                    icon: _jsx(EnvelopeIcon, { className: "w-5 h-5 inline-block" })
                },
                {
                    name: t('app.apps.drive.title'),
                    url: getUrl(appsUrl, appsActiveTabs.drive),
                    current: isCurrentUrl(getUrl(appsUrl, appsActiveTabs.drive)),
                    icon: _jsx(GoogleDriveIcon, {})
                },
                {
                    name: t('app.apps.meeting.title'),
                    url: getUrl(appsUrl, appsActiveTabs.meet),
                    current: isCurrentUrl(getUrl(appsUrl, appsActiveTabs.meet)),
                    icon: _jsx(GoogleMeetIcon, {})
                },
                {
                    name: t('app.apps.chat.title'),
                    url: getUrl(appsUrl, appsActiveTabs.chat),
                    current: isCurrentUrl(getUrl(appsUrl, appsActiveTabs.chat)),
                    icon: _jsx(GoogleChatIcon, {})
                },
                {
                    name: t('app.apps.gemini.title'),
                    url: getUrl(appsUrl, appsActiveTabs.gemini),
                    current: isCurrentUrl(getUrl(appsUrl, appsActiveTabs.gemini)),
                    icon: _jsx(GeminiIcon, {})
                },
                {
                    name: t('app.driveAdvancedSearch.title'),
                    url: getUrl(appsUrl, appsActiveTabs.driveAdvancedSearch),
                    current: isCurrentUrl(getUrl(appsUrl, appsActiveTabs.driveAdvancedSearch)),
                    icon: _jsx(SearchDriveIcon, {})
                },
                {
                    name: t('app.apps.searchMeetings.title'),
                    url: getUrl(appsUrl, appsActiveTabs.searchMeetings),
                    current: isCurrentUrl(getUrl(appsUrl, appsActiveTabs.searchMeetings)),
                    icon: _jsx(SearchMeetIcon, {})
                },
                {
                    name: t('eventsLog.title'),
                    url: getUrl(appsUrl, appsActiveTabs.eventsLog),
                    current: isCurrentUrl(getUrl(appsUrl, appsActiveTabs.eventsLog)),
                    icon: _jsx(EventLogIcon, { className: "w-5 h-5 inline-block" })
                }
            ]
        },
        {
            key: 'doc-alerts',
            name: t('menu.docsAlerts'),
            url: getUrl(docAlertsUrl),
            current: currentUrl.startsWith(docAlertsUrl),
            children: [
                {
                    name: t('docAlerts.docAlerts'),
                    url: getUrl(docAlertsUrl, docAlertsActiveTabs.alerts),
                    current: isCurrentUrl(getUrl(docAlertsUrl, docAlertsActiveTabs.alerts)),
                    icon: _jsx(BellIcon, { className: "w-5 h-5 inline-block" })
                },
                {
                    name: t('docAlerts.docAlertsConfigTitleTab'),
                    url: getUrl(docAlertsUrl, docAlertsActiveTabs.configuration),
                    current: isCurrentUrl(getUrl(docAlertsUrl, docAlertsActiveTabs.configuration)),
                    icon: _jsx(BellSettingsIcon, { width: "20", height: "20" })
                }
            ],
            scope: ScopeUIPageEnum.PAGE_DOC_ALERTS
        },
        {
            key: 'setting',
            name: t('menu.settings'),
            url: settingsUrl,
            current: currentUrl.startsWith(settingsUrl),
            children: [
                {
                    name: 'General',
                    url: getUrl(settingsUrl, settingsActiveTabs.general),
                    current: isCurrentUrl(getUrl(settingsUrl, settingsActiveTabs.general)),
                    icon: _jsx(Cog6ToothIcon, { className: "w-5 h-5 inline-block" })
                },
                {
                    name: 'Teams/Users',
                    url: getUrl(settingsUrl, settingsActiveTabs.users),
                    current: isCurrentUrl(getUrl(settingsUrl, settingsActiveTabs.users)),
                    icon: _jsx(UsersIcon, { className: "w-5 h-5 inline-block" })
                },
                {
                    name: 'Billing',
                    url: getUrl(settingsUrl, settingsActiveTabs.billing),
                    current: isCurrentUrl(getUrl(settingsUrl, settingsActiveTabs.billing)),
                    icon: _jsx(CreditCardIcon, { className: "w-5 h-5 inline-block" })
                },
                {
                    name: 'Activity Labels',
                    url: getUrl(settingsUrl, settingsActiveTabs.activityLabels),
                    current: isCurrentUrl(getUrl(settingsUrl, settingsActiveTabs.activityLabels)),
                    icon: _jsx(TagIcon, { className: "w-5 h-5 inline-block" })
                }
            ],
            scope: ScopeUIPageEnum.PAGE_SETTINGS
        }
    ].filter(function (item) {
        if (!item.scope)
            return true;
        return hasScopeFun(item.scope, scopes);
    });
};
